import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import clsx from 'clsx'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { processEnv } from 'components/configuration/environmentVariables'
import {
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, {
    ComponentType,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
} from 'react'
import { LRUCache } from 'shared/utils/lruCache'
import { useHoveredNode } from '../HoveredItemStore'
import { useShallowSelector } from '../Workspace2Store'
import ProjectProperties from '../components/ProjectProperties'
import { NodeTitleProps } from '../components/TreeView'
import WorkspaceTitle from '../components/WorkspaceTitle'
import { withLazyLoad } from '../components/withLazyComponent'
import { findNodeById } from '../features/promoteDemote/utils'
import { useTreeViewStyles } from '../styles'
import { DataNode } from '../types'
import { getNodeOrderLabel } from '../utils/nodeOrderUtils'
import {
    filterUniqueNodes,
    getFilteredNodeArrays,
} from '../utils/workspaceEditorUtils'
import { isManufacturerOrBODHeader } from '../utils/manufacturerBodUtils'

/** Interface for TreeDataTransformerParams */
interface TreeDataTransformerParams {
    /** Array of tree nodes */
    treeData: DataNode[]
    /** Object containing CSS classes used for styling the TreeView */
    classes: ReturnType<typeof useTreeViewStyles>
    /** Optional tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
    /** Project ID for header node */
    projectId?: string
    /** Selected project element node for header */
    selectedProjectElementNode?: ProjectElementNode
    /** Loading state for header */
    isLoading?: boolean
    /** Flag indicating if extended view is enabled */
    isExtendedView?: boolean
}

/**
 * Type of Transformed node for the tree
 * @param {ReactNode} title - Content Item to render
 * @param {string} className - CSS class used for styling the node
 * @param {ReactNode} switcherIcon - Icon to render for expand/collapse button
 * @param {ReactNode} icon - Hover icon(s) to render for content
 * @param {TransformedNode} children - Children of the node
 * @param {number} depth - The depth of content in hierarchical tree node.
 */
export type TransformedNode = DataNode & {
    title: ReactNode
    className?: string
    switcherIcon?: ReactNode
    icon?: ReactNode
    children?: TransformedNode[]
    depth?: number
    role?: string
    'data-testid'?: string
    'data-key'?: string | React.Key
    'aria-expanded'?: string
    disabled?: boolean
}

const LazyNodeTitle = withLazyLoad<NodeTitleProps>(() =>
    import('../components/TreeView/NodeTitle').then((module) => ({
        default: module.NodeTitle as ComponentType<any>,
    }))
)

/**
 * Custom hook for transforming tree data into renderable format.
 *
 * Handles duplicate order values from API by:
 * 1. Filtering duplicate nodes using unique keys
 * 2. Creating consistent arrays for numbering calculations
 * 3. Ensuring proper array alignment between ordering and rendering
 *
 * This prevents numbering gaps (1,2,4,6) when API returns duplicate orders (0,1,1,2)
 * by producing consecutive numbering (1,2,3,4) regardless of original order values.
 *
 * @param {TreeDataTransformerParams} - Parameters for the hook
 * @returns {TransformedNode[]} Transformed tree data with proper consecutive numbering
 */
export const useTreeDataTransformer = ({
    treeData,
    classes,
    treeRef,
    projectId,
    selectedProjectElementNode,
    isLoading,
    isExtendedView,
}: TreeDataTransformerParams): TransformedNode[] => {
    const {
        activeTab,
        isContentTeamFromStudioPage,
        expandedKeys,
        contentItemViewNodes,
    } = useShallowSelector((state) => ({
        activeTab: state.activeTab,
        isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
        expandedKeys: state.expandedKeys,
        contentItemViewNodes: state.contentItemViewNodes,
    }))

    const hoveredNode = useHoveredNode()

    const {
        visibilitySettingValues: { areNotesVisible, isInactiveContentVisible },
    } = useShallowEditorActionBar((state) => ({
        visibilitySettingValues: state.visibilitySettingValues,
    }))

    // Performance-optimized caches with LRU eviction
    const classCache = useRef(new LRUCache<string, string>(5000))
    const orderLabelCache = useRef(new LRUCache<string, string>(5000))
    const lastTreeDataRef = useRef<DataNode[]>([])

    // Clear caches when tree data changes significantly
    const clearCaches = useCallback(() => {
        classCache.current.clear()
        orderLabelCache.current.clear()
    }, [])

    // Check if tree data has changed significantly
    const hasTreeDataChanged = useMemo(() => {
        const currentKeys = new Set(treeData.map((node) => node.key))
        const previousKeys = new Set(
            lastTreeDataRef.current.map((node) => node.key)
        )

        if (currentKeys.size !== previousKeys.size) {
            lastTreeDataRef.current = treeData
            return true
        }

        for (const key of currentKeys) {
            if (!previousKeys.has(key)) {
                lastTreeDataRef.current = treeData
                return true
            }
        }

        lastTreeDataRef.current = treeData
        return false
    }, [treeData])

    // Clear caches when tree structure changes
    React.useEffect(() => {
        if (hasTreeDataChanged) {
            clearCaches()
        }
    }, [hasTreeDataChanged, clearCaches])

    const isHovered = (node: DataNode) =>
        (hoveredNode as DataNode)?.key === node.key
    const nodeClass = useCallback(
        (node: DataNode) => {
            const isNodeHovered = isHovered(node)

            // Use separate cache keys for hovered and non-hovered states
            const baseKey = `${node.key}_${node.type}`
            const hoveredKey = `${baseKey}_hovered`

            // Check cache first - use peek() to avoid expensive LRU reordering on hover
            if (isNodeHovered) {
                const cached = classCache.current.peek(hoveredKey)
                if (cached !== undefined) return cached
            } else {
                const cached = classCache.current.peek(baseKey)
                if (cached !== undefined) return cached
            }

            // Compute class name only if not cached
            const className = clsx(
                node.type === NODE_TYPES.Article && classes.articleContainer,
                isNodeHovered && 'hovered'
            )

            // Store in appropriate cache key - use set() for proper LRU management
            const cacheKey = isNodeHovered ? hoveredKey : baseKey
            classCache.current.set(cacheKey, className)
            return className
        },
        [classes.articleContainer, isHovered]
    )

    // Memoized order label calculation
    const getOrderLabel = useCallback(
        (node: DataNode, visibleNodes: DataNode[], depth: number) => {
            // Add node index to cache key to invalidate cache when position changes during drag/drop or move operations
            const nodeIndex = visibleNodes.findIndex((n) => n.key === node.key)
            const cacheKey = `${node.key}_${visibleNodes.length}_${depth}_${activeTab}_${nodeIndex}`

            // Use peek() for read-heavy operations to avoid LRU reordering overhead
            const cachedValue = orderLabelCache.current.peek(cacheKey)
            if (cachedValue !== undefined) {
                return cachedValue
            }

            const orderLabel = getNodeOrderLabel(
                node,
                visibleNodes,
                depth,
                activeTab,
                contentItemViewNodes
            )

            // Use set() only when adding new entries for proper LRU management
            orderLabelCache.current.set(cacheKey, orderLabel)
            return orderLabel
        },
        [activeTab, contentItemViewNodes]
    )

    // Filter out manufacturers that are not selected
    const isManufacturerVisible = useCallback(
        (node: DataNode) => {
            // Only process manufacturer/BOD nodes
            if (!node.isManufacturers && !node.isBOD) {
                return true  // Show all non-manufacturer/BOD nodes
            }

            // Don't show hidden nodes
            if (node.isHidden) {
                return false
            }

            // Check if it's a header using centralized utility
            if (isManufacturerOrBODHeader(node)) {
                return true  // Always show headers
            }

            // It's a pill (actual manufacturer item), check if it's selected
            const contentItem = node.contentItemNodes?.[0]
            const parentNode =
                findNodeById(contentItemViewNodes, contentItem?.id ?? '') || null
            // Only return true if isPillSelected is exactly true
            return parentNode?.isPillSelected === true
        },
        [contentItemViewNodes]
    )

    // Append Product Library Button Node after the Manufacturers in AE Workspace
    const getChildrenWithProductLibraryButton = useCallback(
        (node: DataNode) => {
            // Only process manufacturer nodes
            if (!node.isManufacturers) {
                return node.children || []
            }

            // Check if it's a manufacturer header using centralized utility
            const isManufacturerHeader = isManufacturerOrBODHeader(node)

            if (
                isManufacturerHeader &&
                processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                    '1' &&
                !isContentTeamFromStudioPage
            ) {
                const children = node.children ? [...node.children] : []
                const hasButton = children.some(
                    (child) => child.key === 'product-library-btn'
                )
                if (!hasButton) {
                    const productLibraryButton: DataNode = {
                        order: children.length,
                        children: [],
                        key: 'product-library-btn',
                        contentItemNodes: [],
                        type: 'Paragraph',
                    }
                    return [...children, productLibraryButton]
                }
                return children
            }
            return node.children || []
        },
        [isContentTeamFromStudioPage]
    )

    return useMemo(() => {
        /**
         * Set to track unique node keys across the entire tree traversal.
         * This prevents duplicate nodes from being included in the transformed output,
         * which could cause numbering inconsistencies when API returns duplicate order values.
         */
        const nodeKeys = new Set<string>()

        /**
         * Recursively transforms tree nodes with proper duplicate handling and consecutive numbering.
         *
         * The transformation process:
         * 1. Filter duplicate nodes using unique keys (prevents race conditions)
         * 2. Create separate arrays for numbering calculation vs. display
         * 3. Use the same visibleNodesForOrdering array for all numbering calculations
         *
         * This ensures that nodes with duplicate API order values (0,1,1,2)
         * produce consecutive display numbering (1,2,3,4) instead of gaps (1,2,4,6).
         */
        const transform = (
            nodes: DataNode[],
            depth: number = 0
        ): TransformedNode[] => {
            if (!nodes || nodes.length === 0) {
                return []
            }

            // Step 1: Filter out duplicate nodes using utility function
            const uniqueAllNodes = filterUniqueNodes(nodes, nodeKeys)

            // Step 2: Get filtered arrays ensuring alignment between numbering and display
            const {
                visibleNodesForOrdering,
                nodesToDisplay,
            } = getFilteredNodeArrays(uniqueAllNodes, isInactiveContentVisible)

            // Filter out unselected manufacturers in AE workspace only.
            const filteredNodesToDisplay =
                processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                    '1' && !isContentTeamFromStudioPage
                    ? nodesToDisplay.filter(isManufacturerVisible)
                    : nodesToDisplay

            return filteredNodesToDisplay.map((node) => {
                // Appended ProductLibraryButton after the Manufacturers
                const childrenWithButton = getChildrenWithProductLibraryButton(
                    node
                )

                /**
                 * Generate order label using the consistent visibleNodesForOrdering array.
                 * This array is used for ALL numbering calculations to ensure:
                 * - No gaps in numbering when duplicates are filtered
                 * - Consistent indexing across visible and hidden nodes
                 * - Proper consecutive numbering (1,2,3,4) regardless of API order values
                 */
                const orderLabel = getOrderLabel(
                    node,
                    visibleNodesForOrdering, // Always use same array for consistency
                    depth
                )

                return {
                    ...node,
                    role: 'treeitem',
                    'data-testid': 'tree-child',
                    'data-key': node.key,
                    'aria-expanded': node.isLeaf ? 'false' : 'true',
                    className: nodeClass(node),

                    title: (
                        <LazyNodeTitle
                            node={node}
                            isExpanded={expandedKeys?.includes(node.key)}
                            showNodeOrder={true}
                            orderLabel={orderLabel}
                            classes={classes}
                            areNotesVisible={areNotesVisible}
                            treeRef={treeRef}
                        />
                    ),
                    disabled: isContentTeamFromStudioPage
                        ? false
                        : node.isHidden,
                    depth,
                    children: transform(childrenWithButton || [], depth + 1), // Increment depth for children
                    ...(node.key === 'product-library-btn'
                        ? { draggable: false }
                        : {}),
                }
            })
        }
        if (isExtendedView) {
            /**
             * Create header node with WorkspaceTitle and ProjectProperties.
             * This special node is always rendered first (order: -1) and scrolls with tree content.
             * It's disabled to prevent interaction and uses a unique key to identify it.
             */
            const headerNode: TransformedNode = {
                key: '__header_node__',
                type: 'Paragraph', // Use existing type instead of creating new 'Header' type
                contentItemNodes: [],
                order: -1,
                isLeaf: false,
                disabled: true,
                className: 'tree-header-node',
                title: (
                    <div
                        data-testid="tree-header-node"
                        style={{
                            position: 'relative',
                            width: '100%',
                        }}
                    >
                        <WorkspaceTitle projectId={projectId} />
                        <ProjectProperties
                            projectElementNode={selectedProjectElementNode}
                            projectId={projectId ?? ''}
                            isLoading={isLoading ?? false}
                        />
                    </div>
                ),
            }

            // Prepend header node to transformed tree data
            return [headerNode, ...transform(treeData)]
        }
        return transform(treeData)
    }, [
        treeData,
        activeTab,
        expandedKeys,
        classes,
        isInactiveContentVisible,
        areNotesVisible,
        isContentTeamFromStudioPage,
        projectId,
        selectedProjectElementNode,
        isLoading,
        isExtendedView,
        // Note: nodeClass and getOrderLabel are callbacks with their own dependencies
        // and don't need to be included here
    ])
}
